/**
 * The read-only HTTP surfaces the terminal uses.
 *
 * Paths are **same-origin**: `/api/access`, `/api/history`, `/api/trade` are
 * proxied by Vite in development (and by the reverse proxy in a deployment) to
 * `:18080`, `:18083` and `:18084`. That keeps the browser away from CORS, keeps
 * the access token out of a cross-site context, and mirrors the real topology —
 * the terminal talks to the edge, not to three services by port.
 *
 * The WebSocket is `/ws` (→ access `:18090`).
 *
 * Every response is untrusted input: the preview is validated against the schema
 * in `@weke/protocol` (`parsePreviewResponse`), and the history rows are checked
 * for the fields the UI needs before they reach state.
 */

import { parsePreviewResponse } from '@weke/protocol';
import type { PreviewRequest, PreviewResponse } from '@weke/protocol';

import { sessionFromLogin } from './session.js';
import type { Session } from './session.js';
import type { DealPage, DealRecord, MinuteBar } from '../data/types.js';

export const API = {
  access: '/api/access',
  history: '/api/history',
  trade: '/api/trade',
  ws: '/ws',
} as const;

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

interface ErrorBody {
  error?: { code?: unknown; message?: unknown };
}

async function failure(response: Response): Promise<ApiError> {
  let code = `HTTP_${response.status}`;
  let message = response.statusText;
  try {
    const body = (await response.json()) as ErrorBody;
    if (typeof body.error?.code === 'string') code = body.error.code;
    if (typeof body.error?.message === 'string') message = body.error.message;
  } catch {
    // A non-JSON error body keeps the status as its explanation.
  }
  return new ApiError(response.status, code, message);
}

async function getJson<T>(path: string, token?: string): Promise<T> {
  const response = await fetch(path, {
    headers:
      token === undefined
        ? {}
        : { authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw await failure(response);
  return (await response.json()) as T;
}

/** `POST /auth/login` — the tenant is chosen at the edge via `X-Tenant`. */
export async function login(
  tenantSlug: string,
  loginValue: string,
  password: string,
): Promise<Session> {
  const response = await fetch(`${API.access}/auth/login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-tenant': tenantSlug },
    body: JSON.stringify({ login: loginValue, password }),
  });
  if (!response.ok) throw await failure(response);
  const body = (await response.json()) as Parameters<typeof sessionFromLogin>[0];
  return sessionFromLogin(body, loginValue, Date.now());
}

/** `POST /auth/refresh` — silent token renewal, keeping the trader's login. */
export async function refresh(session: Session): Promise<Session> {
  const response = await fetch(`${API.access}/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refresh_token: session.refreshToken }),
  });
  if (!response.ok) throw await failure(response);
  const body = (await response.json()) as Parameters<typeof sessionFromLogin>[0];
  return sessionFromLogin(body, session.login, Date.now());
}

/**
 * `POST /preview` — the account snapshot, symbols, and (optionally) the engine's
 * answer to a hypothetical order. This is the only source of margin numbers.
 */
export async function fetchPreview(token: string, place?: PreviewRequest['place']): Promise<PreviewResponse> {
  const response = await fetch(`${API.trade}/preview`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify(place === undefined ? {} : { place }),
  });
  if (!response.ok) throw await failure(response);
  return parsePreviewResponse(await response.json());
}

/** `GET /history/deals` — paginated by `account_seq`. */
export async function fetchDeals(
  token: string,
  options: { afterSeq?: string; limit?: number } = {},
): Promise<DealPage> {
  const query = new URLSearchParams();
  query.set('after_seq', options.afterSeq ?? '0');
  query.set('limit', String(options.limit ?? 200));
  const body = await getJson<DealPage>(`${API.history}/history/deals?${query.toString()}`, token);
  return {
    deals: Array.isArray(body.deals) ? body.deals : [],
    next_after_seq: body.next_after_seq ?? null,
    has_more: body.has_more === true,
  };
}

/**
 * Every deal on the account, following `has_more` up to a bounded number of
 * pages. The statement's running balance needs the whole ledger from `seq` 1
 * (see `lib/csv.ts`), and a bounded walk keeps a runaway account from freezing
 * the tab.
 */
export async function fetchAllDeals(token: string, maxPages = 20): Promise<DealRecord[]> {
  const out: DealRecord[] = [];
  let afterSeq = '0';
  for (let page = 0; page < maxPages; page += 1) {
    const result = await fetchDeals(token, { afterSeq, limit: 500 });
    out.push(...result.deals);
    if (!result.has_more || result.next_after_seq === null) break;
    afterSeq = result.next_after_seq;
  }
  return out;
}

/**
 * `GET /history/statement` — the official, server-generated statement.
 *
 * The bytes are a pure fold over the journal (`services/recon`), not the
 * client-side CSV: the browser asks for a file and triggers a download, and the
 * server's `content-disposition` names it. A failure is an `ApiError` with the
 * server's code, so the caller can put it in a banner.
 */
export async function downloadStatementFile(
  token: string,
  format: 'csv' | 'pdf' | 'json' = 'pdf',
): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`${API.history}/history/statement?format=${format}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw await failure(response);
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  return {
    blob: await response.blob(),
    filename: match?.[1] ?? `weke-statement.${format}`,
  };
}

/** `GET /history/bars` — M1 bars for a range, oldest first. */
export async function fetchBars(
  token: string,
  options: { symbol: string; from: string; to: string; limit: number },
): Promise<MinuteBar[]> {
  const query = new URLSearchParams({
    symbol: options.symbol,
    from: options.from,
    to: options.to,
    limit: String(options.limit),
  });
  const body = await getJson<{ bars?: MinuteBar[] }>(
    `${API.history}/history/bars?${query.toString()}`,
    token,
  );
  const bars = Array.isArray(body.bars) ? body.bars : [];
  return bars.filter(
    (bar) =>
      typeof bar.minute_ts === 'string' &&
      typeof bar.open_e8 === 'string' &&
      typeof bar.high_e8 === 'string' &&
      typeof bar.low_e8 === 'string' &&
      typeof bar.close_e8 === 'string',
  );
}

/**
 * `POST /history/bars/rebuild` — materialise M1 bars from the stored ticks.
 *
 * The history service writes ticks continuously but only *derives* bars when
 * asked (P4 shipped the endpoint, not a scheduler), so a freshly provisioned
 * stack has ticks and no candles. The chart asks for one rebuild per symbol when
 * it finds none, which is idempotent (`rebuildBars` replaces the range from the
 * ticks, the referee) and touches no ledger state. Documented in
 * `docs/12-p5-handoff.md` as the one place the terminal asks the read model to
 * do work.
 */
export async function rebuildBars(
  token: string,
  options: { symbol: string; from: string; to: string },
): Promise<void> {
  const response = await fetch(`${API.history}/history/bars/rebuild`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify(options),
  });
  if (!response.ok) throw await failure(response);
}

